import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { TrackedInput, TrackedInputProps } from "./TrackedInput";

export type TextFieldSize = "s" | "m" | "h5" | "h4" | "h3" | "h2" | "h1";

interface TextFieldProps extends Omit<TrackedInputProps, "size"> {
  size?: TextFieldSize;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
}

export const TEXT_FIELD_BASE_CLASSES =
  "w-full border border-black-tint-90 text-black-tint-20 placeholder:text-black-tint-40 focus:border-black-tint-55 " +
  "disabled:cursor-not-allowed disabled:bg-black-tint-97 disabled:border-black-tint-97 disabled:focus:border-black-tint-97 disabled:text-black-tint-70 disabled:placeholder:text-black-tint-70 " +
  "read-only:bg-black-tint-97 read-only:border-black-tint-97 read-only:focus:border-black-tint-97 " +
  "aria-invalid:border-red-shade-20 aria-invalid:bg-red-tint-93 aria-invalid:focus:border-red-shade-20 " +
  "disabled:aria-invalid:border-black-tint-97 disabled:aria-invalid:bg-black-tint-97 disabled:aria-invalid:focus:border-black-tint-97 " +
  "supports-squircle:corner-squircle";

const SIZE_CLASSES: Record<TextFieldSize, string> = {
  s: "body-s rounded-[8px] supports-squircle:rounded-[16px] px-2.5 py-2",
  m: "body-m rounded-[8px] supports-squircle:rounded-[16px] px-3 py-2",
  h5: "h5 rounded-[6px] supports-squircle:rounded-[12px] px-2 py-1",
  h4: "h4 rounded-[8px] supports-squircle:rounded-[16px] px-2.5 py-1.5",
  h3: "h3 rounded-[8px] supports-squircle:rounded-[16px] px-3 py-2",
  h2: "h2 rounded-[10px] supports-squircle:rounded-[20px] px-[14px] py-2.5",
  h1: "h1 rounded-[12px] supports-squircle:rounded-[24px] px-4 py-3",
};

const ICON_START_CLASSES: Record<TextFieldSize, string> = {
  s: "left-2.5 [&_svg]:!size-3.5",
  m: "left-3 [&_svg]:!size-4",
  h5: "left-2 [&_svg]:!size-3",
  h4: "left-2.5 [&_svg]:!size-3.5",
  h3: "left-3 [&_svg]:!size-4",
  h2: "left-[14px] [&_svg]:!size-5",
  h1: "left-4 [&_svg]:!size-6",
};

const ICON_END_CLASSES: Record<TextFieldSize, string> = {
  s: "right-2.5 [&_svg]:!size-3.5",
  m: "right-3 [&_svg]:!size-4",
  h5: "right-2 [&_svg]:!size-3",
  h4: "right-2.5 [&_svg]:!size-3.5",
  h3: "right-3 [&_svg]:!size-4",
  h2: "right-[14px] [&_svg]:!size-5",
  h1: "right-4 [&_svg]:!size-6",
};

const ICON_START_INPUT_CLASSES: Record<TextFieldSize, string> = {
  s: "pl-8",
  m: "pl-9",
  h5: "pl-[26px]",
  h4: "pl-8",
  h3: "pl-9",
  h2: "pl-[42px]",
  h1: "pl-12",
};

const ICON_END_INPUT_CLASSES: Record<TextFieldSize, string> = {
  s: "pr-8",
  m: "pr-9",
  h5: "pr-[26px]",
  h4: "pr-8",
  h3: "pr-9",
  h2: "pr-[42px]",
  h1: "pr-12",
};

export const TextField: FC<TextFieldProps> = ({
  size = "s",
  className,
  iconStart,
  iconEnd,
  ...props
}) => {
  const input = (
    <TrackedInput
      className={twMerge(
        TEXT_FIELD_BASE_CLASSES,
        SIZE_CLASSES[size],
        iconStart && ICON_START_INPUT_CLASSES[size],
        iconEnd && ICON_END_INPUT_CLASSES[size],
        className,
      )}
      {...props}
    />
  );

  if (!iconStart && !iconEnd) {
    return input;
  }

  return (
    <div className="relative w-full text-black-tint-20 has-[:disabled]:text-black-tint-70">
      {iconStart && (
        <span
          aria-hidden="true"
          className={twMerge(
            "pointer-events-none absolute inset-y-0 flex items-center",
            ICON_START_CLASSES[size],
          )}
        >
          {iconStart}
        </span>
      )}
      {input}
      {iconEnd && (
        <span
          aria-hidden="true"
          className={twMerge(
            "pointer-events-none absolute inset-y-0 flex items-center",
            ICON_END_CLASSES[size],
          )}
        >
          {iconEnd}
        </span>
      )}
    </div>
  );
};
